import {Badge} from '@common-web/components/ui/badge';
import {ROLES} from '@common/roles';

// Flat pill palette mirroring the demo's roleBadge() semantics:
// admins = filled navy, client = soft blue, broker = soft emerald,
// group = soft amber, other internal roles = soft gray, custom roles = soft amber.
const USER_TYPE_BADGE_CONFIG: Record<string, {className: string; label: string}> = {
  INTERNAL: {
    className: 'bg-brand-navy text-white border-0 font-medium',
    label: 'Internal',
  },
  CLIENT: {
    className: 'bg-brand-blue/10 text-brand-navy border-0 font-medium',
    label: 'Client Admin',
  },
  GROUP: {
    className: 'bg-amber/10 text-amber border-0 font-medium',
    label: 'Group Access',
  },
  BROKER: {
    className: 'bg-emerald/10 text-emerald border-0 font-medium',
    label: 'Broker',
  },
};

export function getUserTypeBadge(type: string) {
  const config = USER_TYPE_BADGE_CONFIG[type];
  if (!config) return null;
  return <Badge className={config.className}>{config.label}</Badge>;
}

const ROLE_BADGE_CONFIG: Record<string, {label: string; className: string}> = {
  [ROLES.INTERNAL_ADMIN]: {label: 'Admin', className: 'bg-brand-navy text-white border-0'},
  // Retired role string still present on un-migrated profiles (see the
  // `internal:admin-cbs` alias in @common/roles ROLE_PERMISSIONS).
  'internal:admin-cbs': {label: 'CBS Admin', className: 'bg-brand-navy text-white border-0'},
  [ROLES.INTERNAL_ACCOUNTING]: {
    label: 'Accounting',
    className: 'bg-gray-100 text-gray-600 border-0',
  },
  [ROLES.INTERNAL_ANALYST]: {label: 'Analyst', className: 'bg-gray-100 text-gray-600 border-0'},
  [ROLES.CLIENT_ADMIN]: {
    label: 'Client Admin',
    className: 'bg-brand-blue/10 text-brand-navy border-0',
  },
  'client:super-admin': {
    label: 'Super Admin',
    className: 'bg-brand-navy text-white border-0',
  },
  [ROLES.CLIENT_ELIGIBILITY]: {label: 'Eligibility', className: 'bg-amber/10 text-amber border-0'},
  [ROLES.GROUP_ADMIN]: {
    label: 'Group Admin',
    className: 'bg-brand-blue/10 text-brand-navy border-0',
  },
  [ROLES.CLIENT_ACCOUNTING]: {
    label: 'Client Accounting',
    className: 'bg-emerald/10 text-emerald border-0',
  },
};

/** "internal:my-custom-role" → "My Custom Role" */
function labelFromRoleKey(role: string): string {
  const key = role.split(':').pop() ?? role;
  return key
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getRoleBadge(role: string | null | undefined) {
  if (!role) return null;
  const roleInfo = ROLE_BADGE_CONFIG[role] ?? {
    // Custom roles get the demo's amber "custom" treatment.
    label: labelFromRoleKey(role),
    className: 'bg-amber/10 text-amber border-0',
  };
  return <Badge className={`${roleInfo.className} font-medium`}>{roleInfo.label}</Badge>;
}
